import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLoading } from './LoadingContext';
import './Loader.scss';

// Only show the overlay if loading outlasts this — hides the flash-of-loader
// for fast calls, which is what makes quick pages feel slow.
const SHOW_DELAY = 200;
const HIDE_DURATION = 450;

const Loader = () => {
  const { isGlobalLoading } = useLoading();
  const [phase, setPhase]   = useState('idle'); // idle | pending | visible | leaving
  const timerRef            = useRef(null);
  const phaseRef            = useRef('idle');

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    clearTimeout(timerRef.current);

    if (isGlobalLoading) {
      setPhase('pending');
      timerRef.current = setTimeout(() => setPhase('visible'), SHOW_DELAY);
    } else if (phaseRef.current === 'visible') {
      // Was actually shown to the user — fade it out instead of snapping away.
      setPhase('leaving');
      timerRef.current = setTimeout(() => setPhase('idle'), HIDE_DURATION);
    } else {
      // Finished before it ever became visible — nothing to fade, just cancel.
      setPhase('idle');
    }

    return () => clearTimeout(timerRef.current);
  }, [isGlobalLoading]);

  const showingOverlay = phase === 'visible' || phase === 'leaving';

  // Start the overlay where the main content area starts, so the left sidebar
  // menu stays visible and usable. Measured rather than read from CSS vars
  // because the offset varies (expanded / collapsed / ≤1024px / no sidebar).
  const [leftOffset, setLeftOffset] = useState(0);
  useLayoutEffect(() => {
    if (!showingOverlay) return;
    const measure = () => {
      const left = document.querySelector('.main-content')?.getBoundingClientRect().left;
      setLeftOffset(left > 0 ? left : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [showingOverlay]);

  useEffect(() => {
    document.body.classList.toggle('glloading', showingOverlay);
    return () => document.body.classList.remove('glloading');
  }, [showingOverlay]);

  if (!showingOverlay) return null;

  return (
    <div
      style={{ left: leftOffset }}
      className={`gl-overlay${phase === 'leaving' ? ' gl-overlay--leaving' : ''}`}
      role="status"
      aria-label="Loading"
      aria-live="polite"
    >
      <div className="gl-content">
        <div className="gl-dots" aria-hidden="true">
          <span className="gl-dot" />
          <span className="gl-dot" />
          <span className="gl-dot" />
        </div>
      </div>
    </div>
  );
};

export default Loader;
